import { useEffect, useRef } from 'react';

export function useCanvasAnimation(
  draw: (ctx: CanvasRenderingContext2D, frameCount: number, canvas: HTMLCanvasElement) => void,
  size: number
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Handle high DPI displays
    const dpr = window.devicePixelRatio || 1;
    // Set actual size in memory (scaled to account for extra pixel density)
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    // Set displayed size
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;

    // Normalize coordinate system to use css pixels
    ctx.scale(dpr, dpr);

    let frameCount = 0;
    let animationFrameId: number;
    let isVisible = false;

    const render = () => {
      if (isVisible) {
        draw(ctx, frameCount, canvas);
        frameCount++;
      }
      animationFrameId = requestAnimationFrame(render);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisible = entry.isIntersecting;
        });
      },
      { threshold: 0 }
    );

    observer.observe(canvas);
    render(); // Start loop

    return () => {
      cancelAnimationFrame(animationFrameId);
      observer.disconnect();
    };
  }, [draw, size]);

  return canvasRef;
}
